<template>
  <v-container fluid justify-start>

    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredKiosks"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      class="elevation-2"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>
        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="appColor_Medium">

          <app-back-button v-bind:color="appColor_Dark" />

          <v-toolbar-title>Kiosks</v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>

            <!-- Kiosk Type Selector -->
            <v-dialog v-model="kioskTypeSelectorDialog" max-width="210px" persistent>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="isSmallDevice"
                  small
                  slot="activator"
                  :color="appColor_Dark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  :disabled="isLoading"
                  v-on="on"
                  >New
                </v-btn>
                <v-btn v-else
                  slot="activator"
                  :color="appColor_Dark"
                  :disabled="isLoading"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >Create Kiosk
                </v-btn>
              </template>
              <kiosk-type-selector
                v-on:close="closeChooseKioskType"
                v-bind:toolbarColor="appColor_Medium"
                v-bind:toolbarColorDark="appColor_Dark"
                v-bind:label="'Kiosk Type'"
              />
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-1">

          <!-- Kiosk Type -->
          <v-col cols="12" md="2">
            <v-select
              v-model="kioskTypeId"
              item-value="id"
              item-text="name"
              :items="kioskTypesForFilter"
              label="Type"
              placeholder="Type"
              :disabled="isLoading"
            />
          </v-col>

          <!-- Facility -->
          <v-col cols="12" md="4">
            <facility-combobox
              v-model="facilityId"
              :options="facilitiesForFilter"
              :label="getExpression('Label', 'Facility')"
              :disabled="isLoading"
              class="ml-3"
            />
          </v-col>

          <!-- Search -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              :disabled="isLoading"
              clearable
              class="ml-3"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.serialNumber="{ item }">
        <span class="text-left">
          <router-link :to="currentRoute">
            <span @click="edit(item)">{{ item.serialNumber }}</span>
          </router-link>
        </span>
      </template>

      <template v-slot:item.facilityName="{ item }">
        <span class="text-left">{{ item.facilityName }}</span>
      </template>

      <template v-slot:item.dashboard="{ item }">
        <v-btn
          :color="getKioskTypeColor(item.kioskTypeId)"
          small
          :loading="isLoadingDashboard && selectedKioskId === item.id"
          :disabled="isLoadingDashboard"
          :dark="!isLoadingDashboard"
          @click="gotoDashboard(item)"
          >
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left" style="white-space: nowrap"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500px" :persistent="true">
      <v-card>
        <v-toolbar dense dark flat :color="toolbarColor">
          <v-toolbar-title>
            <v-icon>edit</v-icon> {{ formTitle }}
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pa-1 pt-2 ma-0">
          <v-container grid-list-md class="pt-0 pb-0">
            <form>
              <!-- Kiosk Name / Serial Number -->
              <v-row no-gutters>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="editedKiosk.serialNumber"
                    label="S/N"
                    v-validate="'required|max:20'"
                    :error-messages="errors.collect('serialNumber')"
                    data-vv-name="serialNumber"
                    clearable
                    ref="serialNumber"
                    >
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="7">
                  <v-text-field
                    v-model="editedKiosk.name"
                    label="Name"
                    v-validate="'max:100'"
                    :error-messages="errors.collect('kioskName')"
                    data-vv-name="kioskName"
                    class="ml-2"
                    clearable
                    >
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Description -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedKiosk.description"
                    label="Description"
                    clearable
                    >
                  </v-text-field>
                </v-col>
              </v-row>
              
              <!-- Facility -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <facility-combobox
                    v-model="editedKiosk.facilityId"
                    :options="facilityOptions"
                    :label="getExpression('Label', 'Facility')"
                  />
                </v-col>
              </v-row>

              <!-- Machine Name -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedKiosk.machineName"
                    label="Machine Name"
                    clearable
                  />
                </v-col>
              </v-row>

              <!-- GENIE -->
              <template v-if="isGenie">

                <!-- Language -->
                <v-row no-gutters>
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedKiosk.languageId"
                      :items="languages"
                      item-value="id"
                      item-text="description"
                      label="Language"
                      v-validate="'required'"
                      :error-messages="errors.collect('language')"
                      data-vv-name="language"
                      >
                    </v-select>
                  </v-col>

                  <!-- Update Level -->
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedKiosk.updateLevel"
                      :items="updateLevels"
                      label="Update Level"
                      :disabled="updateLevels.length <= 1"
                      class="ml-2"
                    />
                  </v-col>

                  <!-- Button Config -->
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedKiosk.phidgetConfigId"
                      :items="genieButtonConfigs"
                      item-value="id"
                      item-text="name"
                      label="Button Config"
                      class="ml-2"
                    />
                  </v-col>
                </v-row>
              </template>

              <!-- ABBY -->
              <template v-else-if="isAbby">

                <!-- Subscription Level / Language-->
                <v-row no-gutters>
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedKiosk.subscriptionLevel"
                      :items="subscriptionLevels"
                      label="Subscription"
                      :disabled="disableSubscription"
                    />
                  </v-col>

                  <v-col cols="12" sm="5">
                    <v-select
                      v-model="editedKiosk.languageId"
                      :items="languages"
                      item-value="id"
                      item-text="description"
                      label="Language"
                      class="ml-2"
                      >
                    </v-select>
                  </v-col>

                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="editedKiosk.archTypeId"
                      :items="archTypes"
                      item-value="id"
                      item-text="shortName"
                      label="Architecture"
                      class="ml-2"
                      >
                    </v-select>
                  </v-col>
                </v-row>

                <!-- Phidget SN / Update Level / Activity Level -->
                <v-row no-gutters>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="editedKiosk.phidgetHubSerialNumber"
                      label="Phidget Serial #"
                      type="number"
                      v-validate="'max:6'"
                      :error-messages="errors.collect('hubSerialNumber')"
                      data-vv-name="hubSerialNumber"
                      :disabled="disableHubSerialNumber"
                      clearable
                    />
                  </v-col>
                        
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedKiosk.updateLevel"
                      :items="updateLevels"
                      label="Update Level"
                      :disabled="updateLevels.length <= 1"
                      class="ml-2"
                    />
                  </v-col>
                    
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedKiosk.activityLevel"
                      :items="isAbby ? activityLevels : []"
                      label="Activity Level"
                      class="ml-2"
                      :disabled="(isAbby ? activityLevels : []).length <= 1"
                    />
                  </v-col>
                </v-row>
              </template>

              <!-- TeamViewer -->
              <v-row v-if="isSuperAdmin" no-gutters>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedKiosk.teamviewerId"
                    label="TeamViewer ID"
                    >
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedKiosk.teamviewerPassword"
                    label="TeamViewer Password"
                    class="ml-2"
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="4">
                  <v-switch
                    v-model="editedKiosk.isAutoUpdates"
                    class="ml-2"
                    >
                    <template v-slot:label>
                      <span class='caption'>Auto-Updates</span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>

              <!-- ABBY -->
              <template v-if="isAbby">
                
                <!-- Ambient Invitations -->
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-select
                      v-model="editedKiosk.ambientMessages"
                      :items="ambientMessages"
                      label="Ambient Invitations"
                      multiple
                      chips>
                    </v-select>
                  </v-col>
                </v-row>
              </template>
            </form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>Cancel</v-btn>
          <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0" large>Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import { AppColor, ImageUri, KioskTypeId, UserRole, SubscriptionLevel, UserIds, KioskArchTypeIds } from '@/config.js'
import FacilityCombobox from '@/components/FacilityCombobox'
import KioskTypeSelector from '@/components/KioskTypeSelector'
import AppDate from  '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'KioskManager',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    FacilityCombobox,
    KioskTypeSelector,
    AppDate,
    AppBackButton
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Abby: AppColor.KioskTypeAbby,
      appColor_AbbyDark: AppColor.KioskTypeAbbyDark,
      appColor_Genie: AppColor.KioskTypeGenieLight,
      appColor_GenieDark: AppColor.KioskTypeGenie,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,
      archTypeId_WIN64: KioskArchTypeIds.WIN64,

      // roles
      role_Admin: UserRole.Admin,

      // images
      imageUri_GenieKiosk: ImageUri.GenieKiosk,
      imageUri_AbbyKiosk: ImageUri.AbbyKiosk,

      // subscription levels
      subscriptionLevel_Basic: SubscriptionLevel.Basic,
      subscriptionLevel_Advanced: SubscriptionLevel.Advanced,
      subscriptionLevel_Unlimited: SubscriptionLevel.Unlimited,

      isLoading: true,
      isLoadingDashboard: false,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      kioskTypeSelectorDialog: false,
      selectedKioskId: null,

      // abby ambient invitations
      ambientInvitations: [],
      ambientMessages: [],
      defaultAmbientMessages: [],

      headers: [
        {
          text: 'S/N',
          value: 'serialNumber',
          align: 'left',
          width: '5%',
          class: 'pr-0'
        },
        {
          text: 'Id',
          value: 'id',
          align: 'left',
          width: '5%'
        },
        {
          text: 'Facility',
          value: 'facilityName',
          align: 'left',
          width: '25%',
          class: 'pr-0'
        },
        {
          text: 'Domain',
          value: 'domainName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Organization',
          value: 'organizationName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          value: 'dashboard',
          sortable: false,
          align: 'center',
          width: '5%'
        },        
        {
          text: 'Created',
          value: 'createDate',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ],
      kiosks: [],
      kioskTypes: [],
      kioskTypeId: 0,
      kioskTypesForFilter: [],
      facilities: [],
      facilitiesForFilter: [],
      facilityId: null,
      noFacility: { id: 0, name: `(${this.getExpression('Label', 'NoFacility')})` },
      domains: [],
      genieButtonConfigs: [],

      genieUpdateLevels: [],
      abby21UpdateLevels: [],
      abby22UpdateLevels: [],
      abby3UpdateLevels: [],
      activityLevels: [],
      subscriptionLevels: [{ 
        value: SubscriptionLevel.Basic,
        text: 'Basic'
      },{ 
        value: SubscriptionLevel.Advanced,
        text: 'Advanced'
      },{ 
        value: SubscriptionLevel.Unlimited,
        text: 'Unlimited'
      }],
      archTypes: [],
      editedIndex: -1,
      editedKiosk: {
        id: -1,
        serialNumber: null,
        name: null,
        description: null,
        kioskTypeId: null,
        archTypeId: null,
        facilityId: null,
        phidgetConfigId: 1,
        phidgetHubSerialNumber: null,
        phidgetVersion: null,
        machineName: null,
        contactUserId: null,
        contactPhone: null,
        contactPosition: null,
        contactAvailability: null,
        teamviewerId: null,
        teamviewerPassword: null,
        languageId: 1,
        updateLevel: null,
        activityLevel: null,
        isAutoUpdates: null,
        subscriptionLevel: null,
        ambientMessages: null
      },
      defaultKiosk: {
        id: -1,
        serialNumber: null,
        name: null,
        description: null,
        kioskTypeId: null,
        archTypeId: null,
        facilityId: null,
        phidgetConfigId: 1,
        phidgetHubSerialNumber: null,
        phidgetVersion: null,
        machineName: null,
        contactUserId: null,
        contactPhone: null,
        contactPosition: null,
        contactAvailability: null,
        teamviewerId: null,
        teamviewerPassword: null,
        languageId: 1,
        updateLevel: null,
        activityLevel: null,
        isAutoUpdates: null,
        subscriptionLevel: null,
        ambientMessages: null
      },
      restoreKiosk: {
        id: -1,
        serialNumber: null,
        name: null,
        description: null,
        kioskTypeId: null,
        archTypeId: null,
        facilityId: null,
        phidgetConfigId: null,
        phidgetHubSerialNumber: null,
        phidgetVersion: null,
        machineName: null,
        teamviewerId: null,
        teamviewerPassword: null,
        contactUserId: null,
        contactPhone: null,
        contactPosition: null,
        contactAvailability: null,
        languageId: null,
        updateLevel: null,
        activityLevel: null,
        isAutoUpdates: null,
        subscriptionLevel: null,
        ambientMessages: null
      },
      dictionary: {
        attributes: {
          email: 'Email'
        },
        custom: {
          serialNumber: {
            required: () => 'S/N is required',
            max: 'S/N cannot be greater than 20 characters'
          },
          kioskName: {
            max: 'Kiosk name cannot be greater than 100 characters'
          },
          hubSerialNumber: {
            max: 'Hub Serial Number cannot contain more than 6 digits'
          },
          language: {
            required: () => 'Language is required'
          }
        }
      },
      superAdmins: UserIds.SuperAdmin
    }
  },
  computed: {
    ...mapGetters(['profile', 'languages', 'currentRoute']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMOment')

      return this.kiosks.length === 0
        ? this.getExpression('Alert', 'NoKiosks') 
        : ''
    },
    formTitle () {
      const verbage = this.editedKiosk.id < 0
        ? 'New'
        : 'Edit'

      const version = this.editedKiosk.phidgetVersion !== null
        ? this.editedKiosk.phidgetVersion === 22
          ? 'v22'
          : 'v21'
        : 'v3 (Phidgetless)'

      const title = this.isAbby
        ? `${verbage} Abby ${version}`
        : `${verbage} Genie`

      return title
    },
    isSuperAdmin () {
      if (this.isNullOrUndefined(this.profile)) return false
      return this.superAdmins.includes(this.profile.id)
    },
    isAbby () {
      return this.editedKiosk.kioskTypeId === this.kioskTypeId_Abby
    },
    isGenie () {
      return this.editedKiosk.kioskTypeId === this.kioskTypeId_Genie
    },
    toolbarColor () {
      return this.isAbby
        ? this.appColor_Abby
        : this.appColor_Genie
    },
    toolbarColorDark () {
      return this.isAbby
        ? this.appColor_AbbyDark
        : this.appColor_GenieDark
    },
    filteredKiosks () {
      if (this.kiosks === null) return []
      if (this.facilities === null) return []

      const facilityId = this.facilityId
      const kioskTypeId = this.kioskTypeId
      const kiosks = this.kiosks.filter(k => {
        return (
          (k.facilityId === facilityId || facilityId === 0) &&
          (k.kioskTypeId === kioskTypeId || kioskTypeId === 0)
        )
      })

      return kiosks
    },
    updateLevels () {
      if (this.editedKiosk.kioskTypeId === null) return []
      return this.editedKiosk.kioskTypeId === this.kioskTypeId_Genie
        ? this.genieUpdateLevels 
        : this.editedKiosk.phidgetVersion === 21
          ? this.abby21UpdateLevels
          : this.editedKiosk.phidgetVersion === 22
            ? this.abby22UpdateLevels
            : this.editedKiosk.phidgetVersion === null
              ? this.abby3UpdateLevels
              : []
    },
    facilityOptions () {
      const facilities = this.facilities.map(f => { return {
          id: f.id,
          name: f.name,
          geographicalDescription: f.geographicalDescription
        }
      })

      return facilities
    },
    disableHubSerialNumber () {
      return this.editedKiosk.phidgetVersion !== 22
    },
    disableSubscription () {
      return this.editedKiosk.kioskTypeId === this.kioskTypeId_Genie
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true

      appService.getKiosksForManager().then((data) => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.kioskTypes = data.kioskTypes
          self.genieUpdateLevels = data.genieUpdateLevels
          self.abby21UpdateLevels = data.abby21UpdateLevels
          self.abby22UpdateLevels = data.abby22UpdateLevels
          self.abby3UpdateLevels = data.abby3UpdateLevels
          self.activityLevels = data.activityLevels

          self.archTypes = data.archTypes
        
          self.kioskTypesForFilter = []
          self.kioskTypesForFilter.push({ id: 0, name: '(All)' })
          data.kioskTypes.forEach((kt) => {
            self.kioskTypesForFilter.push(kt)
          })

          self.facilities = []
          self.facilities.push({ id: 0, name: '(No Facility)', code: '(None)' })
          data.facilities.forEach((f) => {
            f.name = self.getExtendedFacilityName(f)
            self.facilities.push(f)
          })

          self.facilitiesForFilter = []
          self.facilitiesForFilter.push({ id: 0, name: '(All)', code: '(All)' })
          data.facilities.forEach((f) => {
            self.facilitiesForFilter.push(f)
          })
          self.facilityId = 0

          self.domains = data.domains
          self.genieButtonConfigs = data.genieButtonConfigs

          // map kiosks to internal collection
          self.kiosks = data.kiosks.map(k => { return {
            id: k.id,
            name: k.name,
            serialNumber: k.serialNumber,
            description: k.description,
            kioskTypeId: k.kioskTypeId,
            archTypeId: k.archTypeId,
            facilityId: k.facilityId,
            organizationName: self.getOrganizationName(k.facilityId),
            domainName: self.getDomainName(k.facilityId),
            facilityName: self.getFacilityName(k.facilityId),
            facilityLocation: self.getFacilityLocation(k.facilityId),
            phidgetConfigId: k.phidgetConfigId,
            phidgetHubSerialNumber: k.phidgetHubSerialNumber,
            phidgetVersion: k.phidgetVersion,
            machineName: k.machineName,
            contactUserId: k.contactUserId,
            contactPhone: k.contactPhone,
            contactPosition: k.contactPosition,
            contactAvailability: k.contactAvailability,
            teamviewerId: k.teamviewerId,
            teamviewerPassword: k.teamviewerPassword,
            languageId: k.languageId,
            identifier: k.identifier,
            pictureUri: k.pictureUri,
            updateLevel: k.updateLevel,
            isAutoUpdates: k.isAutoUpdates,
            updateInstallDate: k.updateInstallDate,
            activityLevel: k.activityLevel,
            activityInstallDate: k.activityInstallDate,
            mediaSyncDate: k.mediaSyncDate,
            subscriptionLevel: k.subscriptionLevel,
            ambientInvitationIds: k.ambientInvitationIds,
            createDate: k.createDate
          }})

          // abby ambient invitations
          self.ambientInvitations = data.ambientInvitationSelectItems
          self.ambientMessages = self.ambientInvitations.map(i => i.message)
          self.defaultAmbientMessages = self.ambientInvitations.filter(i => i.isDefault).map(i => i.message)
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    edit (item) {
      const self = this
      self.editDialog = true

      setTimeout(() => {    
        self.editedIndex = self.kiosks.findIndex(k => k.id === item.id)
        self.editedKiosk = Object.assign({}, item)
        self.restoreKiosk = Object.assign({}, item)
          
        // setup abby ambient invitation messages for the multi-select combobox
        if (self.editedKiosk.kioskTypeId === self.kioskTypeId_Abby) {
          if (self.editedKiosk.archTypeId === null) {
            self.editedKiosk.archTypeId = self.archTypeId_WIN64
          }
          const ambientInvitationIds = item.ambientInvitationIds

          // if no invitations then assign the defaults
          if (ambientInvitationIds === null) {
            self.editedKiosk.ambientMessages = self.defaultAmbientMessages
          } else {
            // otherwise assign the existing kiosk invitations
            self.editedKiosk.ambientMessages = self.ambientInvitations
              .filter(i => ambientInvitationIds.includes(i.id))
              .map(i => i.message)
          }
        }
      }, 100)
    },

    remove (item) {
      const self = this
      const index = self.kiosks.findIndex(k => k.id === item.id)
      if (index < 0) return

      const serialNumber = self.kiosks[index].serialNumber

      self.$confirm.show({
        title: 'Delete Kiosk?',
        text: `<b>${serialNumber}</b>`,
        onConfirm: () => {

          self.isDeleting = true
          self.validateDelete(item.id, serialNumber).then(result => {
            if (result.errorSeverity !== null) {
              self.isDeleting = false
              self.handleError(result)
            } else {
              const updateByUserId = self.profile.id.replace('auth0|', '')
              appService.deleteKiosk(item.id, updateByUserId).then(data => {
                if (data.errorSeverity !== null) {
                  self.handleError(data)
                } else {
                  self.kiosks.splice(index, 1)
                }
                self.isDeleting = false
              }).catch(e => { self.handleCriticalError(e) })
            }
          })
        }
      })
    },

    validateDelete (id, serialNumber) {
      return new Promise(resolve => {
        const self = this

        self.validateResidents(id, serialNumber).then(data => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            self.validateKioskManagers(id, serialNumber).then(data => {
              resolve(data)
            })
          }
        })
      })
    },

    validateResidents (id, serialNumber) {
      return new Promise(resolve => {
        appService.getKioskResidents(id).then((data) => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            if (data.residents.length === 0) {
              resolve({ errorSeverity: null })
            }

            const residents = data.residents
            var list = ''

            residents.forEach((r) => {
              list += `${r.name}<br/>`
            })

            // pluralize
            const result =
              residents.length === 1
                ? `<b>${serialNumber}</b> still hosts a resident:<br/><br/>` +
                  list +
                  '<br/>Delete or move the resident to another kiosk before attempting to delete this kiosk.'
                : `<b>${serialNumber}</b> is hosting ${residents.length} residents:<br/><br/>` +
                  list +
                  '<br/>Delete or move the above residents to another kiosk before attempting to delete this kiosk.'

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    validateKioskManagers (id, serialNumber) {
      return new Promise (resolve => {

        appService.getKioskManagers(id).then((data) => {
          if (data.errorSeverity > 0) {
            resolve(data)
          } else {
            if (data.users.length === 0) {
              resolve({ errorSeverity: null })
            }

            var list = ''
            const users = data.users

            users.forEach((u) => {
              list += `${u.name}<br/>`
            })

            // pluralize
            const result =
              users.length === 1
                ? `<b>${serialNumber}</b> still has a <i>Kiosk Manager</i> assigned to it:<br/><br/>` +
                  list +
                  `<br/>You must unassign <b>${serialNumber}</b> from this user before this kiosk can be deleted.`
                : `<b>${serialNumber}</b> still has ${users.length} <i>Kiosk Managers</i> associated to it:<br/><br/>` +
                  list +
                  `<br/>You must unassign <b>${serialNumber}</b> from all of the above users before attempting to delete this kiosk.`

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then((valid) => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          
          self.isSaving = true
          if (isEditMode) {
            var editedKiosk = self.kiosks[self.editedIndex]
            Object.assign(editedKiosk, self.editedKiosk)
            const kioskModel = self.getModel(editedKiosk)
            appService.updateKiosk(editedKiosk.id, kioskModel).then((data) => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
                Object.assign(self.kiosks[self.editedIndex], self.restoreKiosk)
              } else {
                self.kiosks[self.editedIndex].organizationName = self.getOrganizationName(data.kiosk.facilityId)
                self.kiosks[self.editedIndex].domainName = self.getDomainName(data.kiosk.facilityId)
                self.kiosks[self.editedIndex].facilityName = self.getFacilityName(data.kiosk.facilityId)
                self.kiosks[self.editedIndex].facilityLocation = self.getFacilityLocation(data.kiosk.facilityId)
                if (self.kiosks[self.editedIndex].kioskTypeId === self.kioskTypeId_Abby) {
                  self.kiosks[self.editedIndex].ambientInvitationIds = data.kiosk.ambientInvitationIds
                }
                self.closeDialog()
              }
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            const kioskModel = self.getModel(self.editedKiosk)
            appService.addKiosk(kioskModel).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                var editedKiosk = self.editedKiosk
                Object.assign(editedKiosk, data.kiosk)
                editedKiosk.organizationName = self.getOrganizationName(editedKiosk.facilityId)
                editedKiosk.domainName = self.getDomainName(editedKiosk.facilityId)
                editedKiosk.facilityName = self.getFacilityName(editedKiosk.facilityId)
                editedKiosk.facilityLocation = self.getFacilityLocation(editedKiosk.facilityId)
                if (editedKiosk.kioskTypeId === self.kioskTypeId_Abby) {
                  editedKiosk.ambientInvitationIds = data.kiosk.ambientInvitationIds
                }
                self.kiosks.unshift(editedKiosk)
              }
              self.isSaving = false              
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (k) {
      const self = this
      
      const archTypeId = k.archTypeId > self.archTypeId_WIN64
        ? k.archTypeId
        : null

      const serialNumber = k.serialNumber.trim()

      const name = k.name !== null
        ? k.name.trim().length > 0
          ? k.name
          : k.serialNumber
        : k.serialNumber.trim()

      const description = k.description !== null
        ? k.description.trim().length > 0
          ? k.description
          : null
        : null

      const hubSerialNumber =
        k.phidgetVersion !== 21 && k.phidgetHubSerialNumber !== null
          ? parseInt(k.phidgetHubSerialNumber)
          : null

      // if an abby kiosk then assemble selected ambient invitations
      var ambientInvitationIds = null
      if (k.kioskTypeId == self.kioskTypeId_Abby) {
        const selectedMessages = k.ambientMessages !== null
          ? k.ambientMessages
          : null
        const selectedInvitations = selectedMessages !== null
          ? self.ambientInvitations.filter(i => selectedMessages.includes(i.message))
          : null
        ambientInvitationIds = selectedInvitations !== null
          ? selectedInvitations.map(i => i.id)
          : null
      }

      const updateLevel = k.updateLevel > 0
        ? k.updateLevel
        : null

      const activityLevel = k.activityLevel > 0
        ? k.activityLevel
        : null

      const model = {
        name: name,
        description: description,
        kioskTypeId: k.kioskTypeId,
        archTypeId: archTypeId,
        machineName: k.machineName,
        serialNumber: serialNumber,
        facilityId: k.facilityId > 0 ? k.facilityId : null,
        phidgetConfigId: k.phidgetConfigId ?? 1,
        phidgetVersion: k.phidgetVersion,
        phidgetHubSerialNumber: k.phidgetVersion === 21
          ? null
          : hubSerialNumber,
        identifier: k.identifier,
        teamviewerId: k.teamviewerId,
        teamviewerPassword: k.teamviewerPassword,
        contactUserId: k.contactUserId,
        contactPhone: k.contactPhone,
        contactPosition: k.contactPosition,
        contactAvailability: k.contactAvailability,
        languageId: k.languageId,
        subscriptionLevel: k.subscriptionLevel,
        updateLevel: updateLevel,
        activityLevel: activityLevel,
        isAutoUpdates: k.isAutoUpdates ?? true,
        ambientInvitationIds: ambientInvitationIds,

        updateByUserId: self.profile.id
      }

      return model
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedKiosk()
      }, 300)
    },

    closeChooseKioskType (kioskTypeId, phidgetVersion) {
      this.kioskTypeSelectorDialog = false
      if (kioskTypeId > 0) {
        this.editedKiosk.kioskTypeId = kioskTypeId
        this.editedKiosk.phidgetVersion = phidgetVersion

      if (!this.isAbby) {
        this.editedKiosk.subscriptionLevel = null
      }

      // if creating an Abby kiosk then assign default invitations
      this.editedKiosk.ambientMessages = this.isAbby && this.editedKiosk.id < 0
        ? this.defaultAmbientMessages
        : null

      this.editedKiosk.updateLevel = this.editedKiosk.id < 0
        ? this.updateLevels[this.updateLevels.length - 1]
        : null

      this.editedKiosk.activityLevel = this.editedKiosk.id < 0
        ? this.activityLevels[this.activityLevels.length - 1]
        : null

        this.editDialog = true    
      }
    },

    gotoDashboard (item) {
      const self = this

      self.isLoadingDashboard = true
      self.selectedKioskId = item.id
      appService.getKioskExtended(item.id).then(data => {
        if (data.errorSeverity !== null) {
          this.handleError(data)
        } else {
          const kiosk = data.kiosk
          this.$store.dispatch('clearActiveKiosk')
          this.$store.dispatch('clearKioskResidents')
          this.$store.dispatch('clearKioskContacts')
          this.$store.dispatch('clearKioskKeyNums')
          this.$store.dispatch('setActiveKiosk', { activeKiosk: kiosk })
          this.$store.dispatch('pushRoute', { name: 'KioskDashboard' })
          this.$router.push({ name: 'KioskDashboard' })
        }
        self.isLoadingDashboard = false
      })
    },

    getKioskTypeColor (typeId) {
      if (typeId === this.kioskTypeId_Abby) {
        return this.appColor_Abby
      } else if (typeId === this.kioskTypeId_Genie) {
        return this.appColor_Genie
      } else return 'grey'
    },

    getOrganizationName (facilityId) {
      const domains = this.domains.filter(d => {
        return d.facilityIds?.includes(facilityId)
      })
      return domains.length > 0 ? domains[0].organizationName : null
    },

    getDomainName (facilityId) {
      const domains = this.domains.filter(d => {
        return d.facilityIds?.includes(facilityId)
      })
      return domains.length > 0 ? domains[0].name : null
    },

    getFacilityName (facilityId) {
      const facilities = this.facilities.filter(f => {
        return f.id === facilityId
      })
      return facilities.length > 0 ? facilities[0].name : null
    },

    getFacilityLocation (facilityId) {
      const facilities = this.facilities.filter(f => {
        return f.id === facilityId
      })
      return facilities.length > 0 ? facilities[0].geographicalDescription : null
    },

    getExtendedFacilityName (f) {
      if (f === null) return null
      var locationDesc = f.geographicalDescription ?? ''
      return locationDesc.length > 0
        ? `${f.name} (${f.geographicalDescription})`
        : f.name
    },

    resetEditedKiosk () {
      this.editedKiosk = Object.assign({}, this.defaultKiosk)
      this.editedIndex = -1
      this.$validator.pause()
      this.$validator.reset()
    },

    customFilter (value, search, item) {
      const serialNumber  = item.serialNumber ?? ''
      const organizationName  = item.organizationName ?? ''
      const domainName  = item.domainName ?? ''
      const facilityName  = item.facilityName ?? ''
      const facilityLocation  = item.facilityLocation ?? ''

      return (
        value != null &&
        search != null &&
        (item.id.toString().indexOf(search) !== -1 ||
        serialNumber.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        organizationName.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        domainName.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        facilityName.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        facilityLocation.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1)
      )
    },

    handleError (result) {
      this.$error.show({
        title:
          result.errorSeverity === 0 ? 'Validation Failed' : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
  },
  watch: {
    editDialog(val) {
      if (val) {
        const self = this
        setTimeout(() => {
          if (self.editedKiosk.id < 0) {
            self.editedKiosk.facilityId = self.facilityId > 0
              ? self.facilityId
              : null
              self.editedKiosk.isAutoUpdates = true

            self.editedKiosk.archTypeId = self.archTypeId_WIN64
          }
          self.$refs.serialNumber.focus()
        }, 300)
      }
    }
  }
}
</script>
